import { AVAILABLE_INTEGRATIONS } from '../../config/integrations';
import type { OverviewSource } from '../../services/sources-api';

export interface IntegrationDisplay {
  name: string;
  logo?: string;
}

export function integrationDisplay(source: OverviewSource): IntegrationDisplay {
  const match =
    AVAILABLE_INTEGRATIONS.find((i) => i.id === source.integrationId) ??
    AVAILABLE_INTEGRATIONS.find((i) => i.id === source.sourceType) ??
    AVAILABLE_INTEGRATIONS.find((i) => i.id.startsWith(source.sourceType));
  return { name: match?.name ?? source.sourceType, logo: match?.logo };
}

export interface SyncDisplay {
  dot: 'ok' | 'warn' | 'off';
  text: string;
  isProblem: boolean;
}

/** One worst-of sync status per spec §5. `formatRelativeTime` comes from TimezoneContext. */
export function syncDisplay(
  source: OverviewSource,
  formatRelativeTime: (iso: string) => string,
): SyncDisplay {
  if (!source.isActive) return { dot: 'off', text: 'Switched off', isProblem: false };
  if (source.mode !== 'ingestion') return { dot: 'ok', text: 'Queried live', isProblem: false };
  if (!source.syncEnabled) return { dot: 'off', text: 'Sync paused', isProblem: false };
  if (['auth_required', 'disconnected'].includes(source.healthStatus)) {
    return { dot: 'warn', text: 'Reconnect needed', isProblem: true };
  }
  if (source.healthStatus === 'error') return { dot: 'warn', text: 'Sync failing', isProblem: true };
  if (source.healthStatus === 'warning') return { dot: 'warn', text: 'Sync issue', isProblem: true };
  if (source.syncStatus === 'syncing') return { dot: 'ok', text: 'Syncing…', isProblem: false };
  const rel = source.lastSyncAt ? formatRelativeTime(source.lastSyncAt) : null;
  if (source.sourceType === 'webhook') {
    return { dot: 'ok', text: rel ? `Event ${rel}` : 'Receiving', isProblem: false };
  }
  return { dot: 'ok', text: rel ? `Synced ${rel}` : 'Not synced yet', isProblem: false };
}

/** A switched-off source isn't an unhealthy connection — it has its own state
 *  and its own call to action (reactivate / change plan), so it never counts. */
export function needsAttention(source: OverviewSource): boolean {
  return source.isActive && source.healthStatus !== 'healthy';
}

export interface AttentionDisplay {
  message: string;
  action: 'reconnect' | 'retry';
}

/**
 * Copy and call to action for a source that isn't healthy. `retry` means the
 * backend is already retrying on its own and a manual sync exists to jump the
 * queue; `reconnect` means no amount of retrying helps until the user
 * re-establishes the credential.
 */
export function attentionDisplay(source: { healthStatus: string; mode: string }): AttentionDisplay {
  const retrying =
    source.mode === 'ingestion' &&
    (source.healthStatus === 'error' || source.healthStatus === 'warning');

  return retrying
    ? {
        message:
          "Sync is failing — new data isn't coming in. We keep retrying automatically. AI can still read what's already stored.",
        action: 'retry',
      }
    : {
        message:
          "This connection needs attention — new data isn't coming in. AI can still read what's already stored.",
        action: 'reconnect',
      };
}

/** Why the source is switched off, in the owner's words. */
export function deactivationCopy(source: OverviewSource): string {
  return source.deactivationReason === 'plan_limit'
    ? "Switched off because your plan's data source limit was reached. Its history is safe — turn it back on to resume syncing and let AI read it again."
    : 'Switched off. Its history is safe — turn it back on to resume syncing.';
}

export function monogram(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

/**
 * Which Setup surface a source gets in the settings drawer, or null for none.
 * `config` renders the adapter's editable settings inline; `webhook` and
 * `inbound_file` deep-link to their dedicated full-page editors.
 *
 * Keep `CONFIG_SOURCE_TYPES` in sync with the backend `getEditableConfigSchema`
 * switch (IntegrationController) — a source only earns the inline form for the
 * source types that return a non-empty schema there.
 */
export type SetupKind = 'config' | 'webhook' | 'inbound_file' | null;

const CONFIG_SOURCE_TYPES = new Set(['clover', 'toast', 'lightspeed-k', 'quickbooks']);

export function setupKind(sourceType: string): SetupKind {
  if (sourceType === 'webhook') return 'webhook';
  if (sourceType === 'inbound_file') return 'inbound_file';
  if (CONFIG_SOURCE_TYPES.has(sourceType)) return 'config';
  return null;
}
